/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/images
 * Password-gated. noindex. Not linked from any public page.
 */
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';
import SEOHead from '../../components/SEOHead';
import ImageEditor from '../../components/ImageEditor';
import {
  useImageSlots,
  saveImageSlot,
  uploadImageFile,
  invalidateSlotCache,
} from '../../lib/image-slots-client';
import type { ImageSlot } from '../../lib/image-slots-client';
import { ArrowLeft, Upload, Edit3, Trash2, Image, Loader } from '../../lib/icons';

// ── Section name map ──────────────────────────────────────────────────────────
const SECTION_LABELS: Record<string, string> = {
  'performance-upgrades': 'Performance Upgrades',
  'custom-controllers':   'Custom Controllers',
  'homepage':             'Homepage',
};

function toTitleCase(str: string) {
  return str.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getSectionLabel(section: string) {
  return SECTION_LABELS[section] ?? toTitleCase(section);
}

// ── Thumbnail component ───────────────────────────────────────────────────────
function SlotThumbnail({ src, alt }: { src: string | null; alt: string }) {
  const isVideo = src && (src.endsWith('.mp4') || src.endsWith('.mov') || src.endsWith('.webm'));

  if (!src) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-950">
        <Image className="h-6 w-6 text-gray-700" />
        <p className="text-[10px] text-gray-600">No image</p>
      </div>
    );
  }

  if (isVideo) {
    return (
      <video
        src={src}
        autoPlay muted loop playsInline
        className="w-full h-full object-cover"
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover"
    />
  );
}

// ── Slot card ─────────────────────────────────────────────────────────────────
function SlotCard({
  slot,
  onEdit,
  onReplace,
  onRemove,
}: {
  slot: ImageSlot;
  onEdit: (slot: ImageSlot) => void;
  onReplace: (slot: ImageSlot, file: File) => void;
  onRemove: (slot: ImageSlot) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      await onReplace(slot, file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 transition-all overflow-hidden flex flex-col">
      {/* Thumbnail */}
      <div className="aspect-video bg-gray-950 overflow-hidden relative">
        <SlotThumbnail src={slot.image_url} alt={slot.alt_text || slot.label} />
        {uploading && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader className="h-6 w-6 text-[#39FF14] animate-spin" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-sm font-semibold text-[#E0E0E0] mb-0.5">{slot.label}</p>
        <p className="text-[10px] text-gray-600 font-mono mb-3">{slot.page_section} / {slot.slot_id}</p>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-auto">
          {/* Replace */}
          <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] border border-gray-700 cursor-pointer transition-all">
            <Upload className="h-3.5 w-3.5" />
            Replace
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={handleFileChange}
            />
          </label>

          {/* Edit */}
          <button
            onClick={() => onEdit(slot)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] border border-gray-700 transition-all"
          >
            <Edit3 className="h-3.5 w-3.5" />
            Edit
          </button>

          {/* Remove */}
          {slot.image_url && (
            <button
              onClick={() => onRemove(slot)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-950/40 hover:bg-red-900/50 text-red-400 border border-red-900/60 transition-all"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function AdminImagesContent() {
  const { slots, loading, error, refetch } = useImageSlots();
  const [editingSlot, setEditingSlot] = useState<ImageSlot | null>(null);
  const [localSlots, setLocalSlots] = useState<ImageSlot[] | null>(null);

  const displaySlots = localSlots ?? slots;

  const updateLocal = (updated: ImageSlot) => {
    setLocalSlots(prev => {
      const base = prev ?? slots;
      return base.map(s => s.slot_id === updated.slot_id ? updated : s);
    });
  };

  // Group by section
  const sections = Array.from(
    new Set(displaySlots.map(s => s.page_section))
  ).sort();

  const handleEdit = (slot: ImageSlot) => {
    setEditingSlot(slot);
  };

  const handleEditorSave = async (updates: Partial<Omit<ImageSlot, 'slot_id' | 'updated_at'>>) => {
    if (!editingSlot) return;

    // If image is a blob URL (local preview), upload it first
    let imageUrl = updates.image_url;
    if (imageUrl && imageUrl.startsWith('blob:')) {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File([blob], 'upload.jpg', { type: blob.type || 'image/jpeg' });
      imageUrl = await uploadImageFile(editingSlot.slot_id, file);
      updates = { ...updates, image_url: imageUrl };
    }

    const updated = await saveImageSlot(editingSlot.slot_id, updates);
    updateLocal(updated);
    invalidateSlotCache();
  };

  const handleReplace = async (slot: ImageSlot, file: File) => {
    const url = await uploadImageFile(slot.slot_id, file);
    const updated = await saveImageSlot(slot.slot_id, { image_url: url });
    updateLocal(updated);
    invalidateSlotCache();
  };

  const handleRemove = async (slot: ImageSlot) => {
    if (!window.confirm(`Remove image from "${slot.label}"? This cannot be undone.`)) return;
    const updated = await saveImageSlot(slot.slot_id, { image_url: null });
    updateLocal(updated);
    invalidateSlotCache();
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      <SEOHead
        title="Image Manager | GameFix Admin"
        description="Internal image management"
        noindex
      />

      {/* Nav */}
      <div className="border-b border-gray-800 px-6 py-4 flex items-center gap-4">
        <Link
          to="/admin/services"
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#E0E0E0] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Services
        </Link>
        <div className="h-4 w-px bg-gray-700" />
        <h1 className="text-lg font-bold">Image Manager</h1>
        <button
          onClick={refetch}
          className="ml-auto text-xs text-gray-500 hover:text-[#E0E0E0] transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-12">
        {loading && (
          <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
            <Loader className="h-5 w-5 animate-spin" />
            <span>Loading image slots...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-950/40 border border-red-800 rounded-xl p-4 text-red-400 text-sm">
            {error}
          </div>
        )}

        {!loading && sections.map(section => {
          const sectionSlots = displaySlots.filter(s => s.page_section === section);
          return (
            <div key={section}>
              <div className="flex items-center gap-4 mb-6">
                <h2 className="text-xl font-bold text-[#E0E0E0]">{getSectionLabel(section)}</h2>
                <div className="h-px flex-1 bg-gray-800" />
                <span className="text-xs text-gray-600">{sectionSlots.length} slots</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {sectionSlots.map(slot => (
                  <SlotCard
                    key={slot.slot_id}
                    slot={slot}
                    onEdit={handleEdit}
                    onReplace={handleReplace}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {!loading && displaySlots.length === 0 && !error && (
          <div className="text-center py-20 text-gray-600">
            <Image className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>No image slots found. Run the migration to seed slots.</p>
          </div>
        )}
      </div>

      {/* Editor modal */}
      {editingSlot && (
        <ImageEditor
          slot={editingSlot}
          onSave={handleEditorSave}
          onClose={() => setEditingSlot(null)}
        />
      )}
    </div>
  );
}

export default function AdminImagesPage() {
  return (
    <AdminLoginGate>
      {isAdminAuthed() && <AdminImagesContent />}
    </AdminLoginGate>
  );
}
